import { useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router";
import { useAuth } from "../components/AuthContext/AuthContext";
import {
  logoutUrl,
  MULTI_DEVICE_LOGIN_MESSAGE,
  refreshTokenUrl,
} from "../utils/constants";
import { XSquare } from "react-bootstrap-icons";

const useApis = (initialUrl, initialMethod = "GET") => {
  const [serverError, setServerError] = useState("");
  const [tokenError, setTokenError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [resultGet, setResultGet] = useState(null);
  const [resultPost, setResultPost] = useState(null);
  const [url, setUrl] = useState(initialUrl);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [message, setMessage] = useState("");
  const [refreshFailed, setRefreshFailed] = useState(false);

  const isRefreshing = useRef(false); // Tracks if refresh is in progress
  const pendingRequests = useRef([]); // Tracks pending requests

  const navigate = useNavigate();
  const auth = useAuth();
  const [method, setMethod] = useState(initialMethod);

  // Function to show toaster notification when token expires
  const showTokenExpiredToaster = () => {
    setTokenError("Session expired. Please Login again!");
    auth?.showToast(
      "Error",
      "Session expired. Please Login again!",
      "../assets/mhc.png",
      {icon: <XSquare />,
      className: auth?.featureFlag && "vh-toaster",}
    );
    setShowError(true);
    setTimeout(() => {
      setShowError(false);
      setServerError("");
    }, 3000);
    auth?.logout();
  };
  // Function to refresh access token
  const refreshAccessToken = useCallback(async () => {
    if (isRefreshing.current || refreshFailed) return;

    isRefreshing.current = true;
    try {
      const response = await axios.post(refreshTokenUrl, {
        refresh: auth.refreshToken,
      });

      const newAccessToken = response.data.access_token;

      // Updates the access token in AuthContext and localStorage
      auth.setAccessToken(newAccessToken);
      localStorage.setItem("access_token", newAccessToken);

      isRefreshing.current = false;
      setRefreshFailed(false);

      // Retries all pending requests with the new access token
      pendingRequests.current.forEach((callback) => callback());
      pendingRequests.current = [];
    } catch (error) {
      isRefreshing.current = false;
      setRefreshFailed(true);
      showTokenExpiredToaster();
      auth.logout();
      navigate("/login");
      throw error;
    }
  }, [auth, navigate, refreshFailed]);

  // Handle successful response data
  const handleResponseData = (response, requestMethod, sanitizedData) => {
    if (requestMethod === "GET") {
      handleGetResponse(response);
    } else {
      handleNonGetResponse(response, sanitizedData);
    }
    handleResponseStatus(response, requestMethod);
    return response.data;
  };

  // Handle GET response data
  const handleGetResponse = (response) => {
    if (response.data.data) {
      setResultGet(response.data.data);
    }

    if (response.data.message) {
      setMessage({ getMessage: response.data.message });
    }
  };

  // Handle non-GET response data
const handleNonGetResponse = (response, sanitizedData) => {
  // Don't block POSTs that intentionally send no body (like your training calls)
  if (response.data?.data) {
    setResultPost(response.data.data);
  } else {
    setResultPost(response.data);   // fallback: use the whole body, e.g. { message: "..." }
  }

  if (response.data?.message) {
    setMessage({ postMessage: response.data.message });
  }
};
  // Handle response status
  const handleResponseStatus = (response, requestMethod) => {
    if (response.status === 200) {
      setShowSuccess(true);
      if (requestMethod !== "GET") {
        setModalShow(true);
      }
    } else {
      setShowError(true);
      if (requestMethod !== "GET") {
        setModalShow(true);
      }
      setServerError(`An unknown error occurred`);
    }
  };

  // Handle error response
  const handleErrorResponse = (
    error,
    requestConfig,
  ) => {
    const { data, customUrl, customMethod, headers } = requestConfig;
    setShowError(true); 
    const errorMessage = extractErrorMessage(error);
    setServerError(errorMessage);
    handleAuthErrors(error, {data, customUrl, customMethod, headers});

    if (error?.response?.status === 503) {
      setServerError(
        "This staging environment is unavailable daily between 8:00 PM and 6:00 AM UTC. Please check back after 6:00 AM UTC.",
      );
      return;
    }

    if (
      errorMessage === MULTI_DEVICE_LOGIN_MESSAGE &&
      error.response?.status === 401
    ) {
      auth?.showToast(
        "Error",
        "You have logged in to another device",
        "../assets/mhc.png",
      {icon: <XSquare />,
      className: auth?.featureFlag && "vh-toaster",}
      );
      auth.logout();
      navigate("/login");
    }
    setServerError(errorMessage);
    
    handleAuthErrors(error, {data, customUrl, customMethod, headers});
  };

  // Extract error message from error response
  const extractErrorMessage = (error) => {
    return (
      error.response?.data?.message ||
      error.response?.data?.error ||
      "An unknown error occurred"
    );
  };

  // Handle authentication-related errors.
  const handleAuthErrors = (error, requestConfig) => {
    const { data, customUrl, customMethod, headers } = requestConfig;
    const status = error.response?.status;
    const responseData = error.response?.data;
    if (status === 401) {
      if (responseData?.token_expired === true || responseData?.token_expired === "True") {
        handleTokenExpired(data, customUrl, customMethod, headers);
      } else if (responseData?.session_expired) {
        auth.openLogoutModal();
      }
    } else if (responseData?.unauthorized) {
      handleUnauthorized();
    } else {
      setServerError(responseData?.message || responseData?.detail);
    }
  };

  // Handle token expired error
  const handleTokenExpired = (data, customUrl, customMethod, headers) => {
    if (refreshFailed) {
      showTokenExpiredToaster();
      return;
    }

    const retryRequest = async () => {
      try {
        await refreshAccessToken();
        await callApi(data, customUrl, customMethod, headers);
      } catch (refreshError) {
        auth.logout();
        navigate("/login");
        throw refreshError;
      }
    };

    pendingRequests.current.push(retryRequest);

    if (!isRefreshing.current) {
      retryRequest().catch(() => {
        // Already handled inside retryRequest (logout + navigate)
      });
    }
  };

  // Handle session expired error
  const handleSessionExpired = () => {
    setServerError("Session has expired. Please log in again");
    setTimeout(() => {
      auth.logout();
      callApi(null, logoutUrl, "POST");
      navigate("/login");
    }, 3000);
  };

  // Handle unauthorized error
  const handleUnauthorized = () => {
    setTimeout(() => {
      auth.logout();
      callApi(null, logoutUrl, "POST");
      navigate("/login");
    }, 3000);
  };

  // Function to call an API
  const callApi = async (
    data = null,
    customUrl = null,
    customMethod = null,
    headers = {},
  ) => {
    setServerError("");
    setResultPost(null); 
    setIsLoading(true);

    const requestUrl = customUrl || url;
    const requestMethod = customMethod || method;
    const sanitizedData = sanitizeData(data);

    const config = {
      url: requestUrl,
      method: requestMethod,
      data: sanitizedData,
      headers: {
        ...headers,
        Authorization: `Bearer ${localStorage.getItem("access_token") || auth.accessToken}`,
      },
    };

    const request = async () => {
      try {
        const response = await axios(config);
        return handleResponseData(response, requestMethod, sanitizedData);
      } catch (error) {
        handleErrorResponse(error, {data, customUrl, customMethod, headers});
      } finally {
        setIsLoading(false);
      }
    };

    return request();
  };

  // Sanitize request data by removing undefined values
  const sanitizeData = (data) => {
    if (!data) return null;

    return Object.fromEntries(
      Object.entries(data).filter(([_, value]) => value !== undefined),
    );
  };

  return {
    callApi,
    setUrl,
    setMethod,
    serverError,
    setServerError,
    tokenError,
    isLoading,
    setIsLoading,
    resultGet,
    setResultGet,
    resultPost,
    setResultPost,
    showSuccess,
    showError,
    modalShow,
    message,
    setModalShow,
    setShowSuccess,
    setShowError,
    toggleShowSuccess: () => setShowSuccess(!showSuccess),
    toggleShowError: () => setShowError(!showError),
  };
};

export default useApis;
